import machinelogic_app_builder from '../legacy/machinelogic_app_builder';

/**
 * Resolves user-defined injected sequences, robot clips, and variables for a given BuildableApp
 * @param {BuildableApp} pBuildableApp
 * @param {MachineAppApplication} pMachineApp
 * @returns {BuildableApp}
 */
export function applyInjectedResources(pBuildableApp, pMachineApp) {
  const lApp = pMachineApp.getInjectableApplication();
  pBuildableApp.addSequence(...getInjectedSequences(lApp));
  pBuildableApp.addRobotClip(...getInjectedRobotClips(lApp));
  getUserDefinedInjectedVariables(pMachineApp).forEach(pInjectedVariable => addInjectedVariableToBuildableApp(pBuildableApp, pInjectedVariable));

  return pBuildableApp;
}

/**
 * Turns an injection point into the list of buildable instructions associated with it.
 * @param {MachineAppApplication} pMachineApp
 * @param {string} pName
 * @returns {BuildableInstruction[]}
 */
export function resolveInjectionPoint(pMachineApp, pName) {
  if (!pMachineApp.getInjectableApplication()) {
    return [];
  }

  const lJson = pMachineApp.getInjectableApplication().toJson();
  if (!lJson || !lJson.sequences) {
    return [];
  }

  const lSequence = lJson.sequences.find(pSequence => pSequence.name === pName);
  if (!lSequence) {
    return [];
  }

  return lSequence.instructions.map(pInstJson => new machinelogic_app_builder.ImmutableInstruction(pInstJson));
}

function addInjectedVariableToBuildableApp (pBuildableApp, pVariableDefinition) {
  switch (pVariableDefinition.type) {
    case "const":
      pBuildableApp.addVariable(pVariableDefinition.name, pVariableDefinition.type, { default_value: Number(pVariableDefinition.default_value) });
      break;
    case "input":
      pBuildableApp.addVariable(pVariableDefinition.name, pVariableDefinition.type, { inputUuid: pVariableDefinition.inputUuid });
      break;
    case "lambda":
      pBuildableApp.addVariable(pVariableDefinition.name, pVariableDefinition.type, { default_value: pVariableDefinition.default_value });
      break;
    default:
      console.error(`Unsupported variable type for injected variable: ${pVariableDefinition}`);
      break;
  }
}

/**
 * Returns a variable definition for each variable defined in the injectable app.
 * @param {MachineAppApplication} pMachineApp
 * @returns {{
 *   name: string,
 *   type: "const" | "input" | "lambda",
 *   default_value?: string | { default_arguments: string, default_function: string; },
 *   inputUuid?: string,
 * }[]} A list of variable definitions.
 */
function getUserDefinedInjectedVariables(pMachineApp) {
  if (!pMachineApp.getInjectableApplication()) {
    return [];
  }

  const lJson = pMachineApp.getInjectableApplication().toJson();
  if (!lJson || !lJson.parameters) {
    return [];
  }

  return lJson.parameters;
}

/**
 * This method resolves sequences that are NOT injectable points to the application.
 * These sequences were explicitly injected into the app by the user.
 * @param {Object} pApp
 * @returns {BuildableSequence[]}
 */
function getInjectedSequences(pApp) {
  if (!pApp || !pApp.listSequences().length) {
    return [];
  }

  // Find any sequence that is not the main sequence and is not an injectable sequence
  const lNonInjectableSequences = pApp.listSequences().filter(pSequenceName => {
    const lSequence = pApp.getSequence(pSequenceName);
    return !lSequence.isInitialSequence() && !lSequence.getIsLocked();
  });

  return lNonInjectableSequences.map(pSequenceName => {
    const lSequence = pApp.getSequence(pSequenceName);
    return new machinelogic_app_builder.ImmutableSequence(lSequence.toJson());
  });
}

function getInjectedRobotClips(pApp) {
  if (!pApp?.robotParameters?.clips) {
    return [];
  }

  return pApp.robotParameters.clips.map(pJson => new machinelogic_app_builder.ImmutableRobotClip(pJson));
}
